import React from 'react'
import { useApp } from '../context/AppContext'
import { ApiError, NetworkError, api } from '../api/client'
import { tokenFromLocation } from '../auth/token'
import './Auth.css'
import AuthLayout, { AuthLogo } from './AuthLayout'

// email: type an address and be emailed a code. code: type the code. A code,
// not a link, because the home-screen app can't use a link opened from Mail:
// it doesn't share Safari's cookies (ADR-015 amendment 2026-10-25).
type Step = 'email' | 'code'

function messageFor(error: unknown, invalid: string): string {
  if (error instanceof ApiError && error.status === 401) return invalid
  if (error instanceof ApiError && error.status === 429) return 'Too many tries. Wait a minute, then try again.'
  if (error instanceof NetworkError) {
    return "Can’t reach Harmovi. Check your connection and try again."
  }
  return 'Something went wrong. Try again.'
}

export const RESEND_REST_MS = 60_000

const badLink = 'This link has expired or was already used.'
const badCode = "That code didn’t work, or it has expired. Check it, or send a new one."

// expired: shown over the open app after her session ended (review
// 2026-10-14). The app stays mounted underneath, so an open sheet keeps what
// she typed; signing in here takes her straight back to it.
export default function Auth({ expired = false }: { expired?: boolean }) {
  const { state, dispatch } = useApp()
  const [linkToken, setLinkToken] = React.useState<string | null>(null)
  const [step, setStep] = React.useState<Step>('email')
  const [email, setEmail] = React.useState(expired ? state.me?.professional.email ?? '' : '')
  const [code, setCode] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  // "Send a new code" rests for a minute after sending one: the API sends
  // one sign-in email a minute per address, so a sooner tap would say
  // "sent" about nothing.
  const [resting, setResting] = React.useState(false)
  const [error, setError] = React.useState('')
  const [notice, setNotice] = React.useState('')

  // Opened through a login link from the local-only `harmovi login-link`
  // command (tests and local runs): keep the token in memory and take it out
  // of the address bar. Signing in still waits for a tap, as a link preview
  // must not use up the token.
  React.useEffect(() => {
    const token = tokenFromLocation(window.location)
    if (token) {
      setLinkToken(token)
      window.history.replaceState(null, '', '/')
    }
  }, [])

  React.useEffect(() => {
    if (!resting) return
    const t = setTimeout(() => setResting(false), RESEND_REST_MS)
    return () => clearTimeout(t)
  }, [resting])

  const goTo = (next: Step) => {
    setStep(next)
    setError('')
    setNotice('')
  }

  const startSession = async (login: () => Promise<void>, invalid: string) => {
    setSubmitting(true)
    setError('')
    setNotice('')
    try {
      await login()
      dispatch({ type: 'SESSION_STARTED', payload: await api.me() })
    } catch (err) {
      setError(messageFor(err, invalid))
      setSubmitting(false)
    }
  }

  const sendEmail = async () => {
    setSubmitting(true)
    setError('')
    setNotice('')
    try {
      await api.requestLoginEmail(email.trim())
      return true
    } catch (err) {
      setError(messageFor(err, 'Enter the email address you use for Harmovi.'))
      return false
    } finally {
      setSubmitting(false)
    }
  }

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (await sendEmail()) {
      setCode('')
      setStep('code')
    }
  }

  const resend = async () => {
    if (await sendEmail()) {
      setCode('')
      setResting(true)
      setNotice('Sent. The newest code is the one that works. You can ask for another in a minute.')
    }
  }

  const signInWithCode = (value: string) => startSession(() => api.loginWithCode(email.trim(), value), badCode)

  const signInWithLink = (e: React.FormEvent) => {
    e.preventDefault()
    if (linkToken) startSession(() => api.login(linkToken), badLink)
  }

  const feedback = (
    <>
      {error && <div className="form-error" role="alert">{error}</div>}
      {notice && <div className="auth-notice" role="status">{notice}</div>}
    </>
  )

  let subtitle: React.ReactNode
  let body: React.ReactNode

  if (linkToken) {
    subtitle = 'Your sign-in link is ready.'
    body = (
      <form onSubmit={signInWithLink}>
        {feedback}
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
        <button
          type="button"
          className="auth-link"
          onClick={() => {
            setLinkToken(null)
            goTo('email')
          }}
        >
          Use my email instead
        </button>
      </form>
    )
  } else if (step === 'code') {
    subtitle = (
      <>
        If <strong>{email.trim()}</strong> has a Harmovi account, we’ve emailed it a 6-digit code.
      </>
    )
    body = (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (code.length !== 6) {
            setError('Enter the 6 digits from the email.')
            return
          }
          signInWithCode(code)
        }}
      >
        <div className="form-group">
          <label htmlFor="login-code">Sign-in code</label>
          <input
            id="login-code"
            className="auth-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            autoFocus
            value={code}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 6)
              setCode(digits)
              setError('')
              // A code filled in from the keyboard's suggestion signs straight in.
              if (digits.length === 6 && !submitting) signInWithCode(digits)
            }}
            placeholder="123456"
          />
        </div>

        {feedback}

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="auth-hint">The code works once, for 15 minutes.</p>
        <div className="auth-links">
          <button type="button" className="auth-link" disabled={submitting || resting} onClick={resend}>
            Send a new code
          </button>
          <button type="button" className="auth-link" onClick={() => goTo('email')}>
            Use a different email
          </button>
        </div>
      </form>
    )
  } else {
    subtitle = "Enter your email and we’ll send you a code to sign in."
    body = (
      <form onSubmit={requestCode}>
        <div className="form-group">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setError('')
            }}
            placeholder="you@example.com"
          />
        </div>

        {feedback}

        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Sending…' : 'Email me a code'}
        </button>
      </form>
    )
  }

  const checking = step === 'code' && !linkToken
  if (expired) {
    return (
      <div className="auth-form login-form">
        <div className="auth-branding">
          <h1 id="session-expired-title">{checking ? 'Check your email' : "You’ve been signed out"}</h1>
          <p className="auth-subtitle">
            {checking ? subtitle : "Sign in again. Anything you hadn’t saved is still here."}
          </p>
        </div>
        {body}
      </div>
    )
  }

  return (
    <AuthLayout>
      <div className="auth-form login-form">
        <AuthLogo />

        <div className="auth-branding">
          <h1>{checking ? 'Check your email' : 'Welcome'}</h1>
          <p className="auth-subtitle">{subtitle}</p>
        </div>

        {body}
      </div>
    </AuthLayout>
  )
}
